import {
  Group,
  Stack,
  Text,
  useMantineColorScheme,
  useMantineTheme,
} from '@mantine/core'
import { IconCalendar, IconCoin } from '@tabler/icons-react'
import { Link } from '@tanstack/react-router'
import dayjs from 'dayjs'

import { usdFormatter } from '@repo/common/helpers/formatters'
import {
  type FundTransactionWithAsset,
  useAdminFundTransactions,
} from '@repo/common/queries/admin/fund/query_fund_transactions.ts'

import {
  column,
  mapColumn,
  TowerDataTable,
  type TowerDataTableColumn,
} from '../ui/TowerDataTable'

const truncateHash = (hash: string | null): string => {
  if (!hash) return 'N/A'
  return `${hash.substring(0, 6)}...${hash.substring(hash.length - 4)}`
}

export interface FundTransactionsTableProps {
  fundId: string
  limit?: number
  showPagination?: boolean
  maxHeight?: number | string
}

export function FundTransactionsTable({
  fundId,
  limit = 20,
  // showPagination = false,
  // maxHeight = 500,
}: FundTransactionsTableProps) {
  const theme = useMantineTheme()
  const { colorScheme } = useMantineColorScheme()
  const isDark = colorScheme === 'dark'

  // Query fund transactions
  const { data, isLoading } = useAdminFundTransactions(fundId, limit)
  const transactions = data?.transactions.nodes || []

  // Define table columns
  const columns: TowerDataTableColumn<FundTransactionWithAsset>[] = [
    column({
      accessor: 'executed_at',
      title: 'At',
      width: '15%',
      render: (ts) => (
        <Text size="sm">
          <IconCalendar size={12} opacity={0.7} />{' '}
          {ts ? dayjs(ts).format('YYYY-MM-DD hh:mm:ss') : 'N/A'}
        </Text>
        // <Group align="center" gap="xs">
        //   <IconCalendar size={16} opacity={0.7} />
        //   <Text size="sm">{date ? formatDate(date) : 'N/A'}</Text>
        // </Group>
      ),
    }),
    column({
      accessor: 'action',
      title: 'Action',
      // width: '15%',
      render: (action) => {
        // let color = 'blue'
        //
        // if (action.includes('BUY') || action.includes('DEPOSIT')) {
        //   color = 'green'
        // } else if (action.includes('SELL') || action.includes('WITHDRAW')) {
        //   color = 'red'
        // } else if (action.includes('FEE')) {
        //   color = 'yellow'
        // }

        if (action === 'take_management_fee') {
          action = 'Management Fee'
        } else {
          // convert snake case to title case / spaces
          action = action
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (char) => char.toUpperCase())
        }

        return (
          <Text>{action}</Text>
          // <Badge color={color} variant={isDark ? 'filled' : 'light'}>
          //   {actionType}
          // </Badge>
        )
      },
    }),
    column({
      accessor: 'dollars',
      title: 'Amount',
      // width: '15%',
      textAlign: 'right',
      render: (dollars) => (
        <Group align="center" gap="xs" justify="flex-end">
          <IconCoin
            size={16}
            style={{ color: theme.colors.green![isDark ? 6 : 7] }}
          />
          <Text size="sm" fw={500}>
            {usdFormatter(dollars)}
          </Text>
        </Group>
      ),
    }),
    // column({
    //   accessor: 'company_transaction',
    //   title: 'Company Details',
    //   width: '25%',
    //   render: (transaction) => {
    //     if (!transaction) return <Text size="sm">N/A</Text>
    //
    //     return (
    //       <Group>
    //         <Text size="sm">
    //           {transaction.side} {transaction.share_count.toLocaleString()} shares
    //           {transaction.stock_type ? ` (${transaction.stock_type})` : ''}
    //           {transaction.spv ? ` via ${transaction.spv}` : ''}
    //         </Text>
    //       </Group>
    //     )
    //   },
    // }),
    // mapColumn({
    //   //accessor: 'company_transaction.side',
    //   accessor: (c) => c?.company_transaction?.side,  // 'company_transaction.side',
    //   title: 'Side',
    //   // width: '10%',
    //   render: (_, record) => {
    //     const transaction = record.company_transaction
    //     if (!transaction) return <Text size="sm">N/A</Text>
    //     return <Text size="sm">{transaction.side}</Text>
    //   },
    // }),
    mapColumn({
      // accessor: 'company_transaction.share_count',
      accessor: (c) => c.asset_transaction?.share_count,
      title: 'Shares',
      // width: '10%',
      textAlign: 'right',
      render: (_, record) => {
        const transaction = record.asset_transaction
        if (!transaction) return <Text size="sm">N/A</Text>
        return (
          <Text size="sm" fw={500}>
            {transaction.share_count.toLocaleString()}
          </Text>
        )
      },
    }),
    mapColumn({
      // accessor: 'company_transaction.total_price',
      accessor: (c) => c.asset_transaction?.total_price,
      title: 'Total Price',
      // width: '15%',
      textAlign: 'right',
      render: (_, record) => {
        const transaction = record.asset_transaction
        if (!transaction) return <Text size="sm">N/A</Text>
        return (
          <Text size="sm" fw={500}>
            {usdFormatter(transaction.total_price)}
          </Text>
        )
      },
    }),
    mapColumn({
      // accessor: 'company_transaction.share_class',
      accessor: (c) => c.asset_transaction?.share_class,
      title: 'Share Class',
      // width: '15%',
      render: (_, record) => {
        const transaction = record.asset_transaction
        if (!transaction || !transaction.share_class)
          return <Text size="sm">N/A</Text>
        return <Text size="sm">{transaction.share_class}</Text>
      },
    }),
    mapColumn({
      // accessor: 'company_transaction.spv',
      accessor: (c) => c.asset_transaction?.spv,
      title: 'SPV',
      // width: '15%',
      render: (_, record) => {
        const transaction = record.asset_transaction
        if (!transaction || !transaction.spv) return <Text size="sm">N/A</Text>
        return <Text size="sm">{transaction.spv}</Text>
      },
    }),
    mapColumn({
      // accessor: 'company_transaction.spv_share_class',
      accessor: (c) => c.asset_transaction?.spv_share_class,
      title: 'SPV Share Class',
      // width: '15%',
      render: (_, record) => {
        const transaction = record.asset_transaction
        if (!transaction || !transaction.spv_share_class)
          return <Text size="sm">N/A</Text>
        return <Text size="sm">{transaction.spv_share_class}</Text>
      },
    }),
    mapColumn({
      accessor: (c) => c.asset_transaction?.fees,
      title: 'Fees',
      textAlign: 'right',
      render: (_, record) => {
        const transaction = record.asset_transaction
        if (!transaction || !transaction.fees) return <Text size="sm">N/A</Text>
        return (
          <Text size="sm" fw={500}>
            {usdFormatter(transaction.fees)}
          </Text>
        )
      },
    }),
    mapColumn({
      accessor: (c) => [
        c.action_blockchain_tx,
        c.action_external_type,
        c.action_external_tx,
      ],
      title: 'Transaction Identifier',
      width: '15%',
      render: ([blockchain_tx, external_type, external_tx]) => {
        if (blockchain_tx) {
          const link = `https://etherscan.io/tx/${blockchain_tx}`
          return (
            <Link to={link} target="_blank">
              <Text size="sm" ff="monospace">
                {truncateHash(blockchain_tx)}
              </Text>
            </Link>
          )
        } else if (external_tx) {
          return (
            <Stack gap="0">
              <Text size="sm" ff="monospace">
                {external_tx}
              </Text>
              <Text size="sm" style={{ textTransform: 'capitalize' }}>
                {external_type ? external_type.replace(/_/g, ' ') : 'N/A'}
              </Text>
            </Stack>
          )
        }
      },
    }),
    // column({
    //   accessor: 'created_at',
    //   title: 'Created',
    //   width: '18%',
    //   render: (createdAt) => (
    //     <Group align="center" gap="xs">
    //       <IconAlertHexagon size={16} opacity={0.7} />
    //       <Text size="sm" c="dimmed">
    //         {createdAt ? formatDate(createdAt) : 'N/A'}
    //       </Text>
    //     </Group>
    //   ),
    // }),
  ]

  return (
    <TowerDataTable
      data={transactions}
      columns={columns}
      emptyMessage="No transactions found"
      isLoading={isLoading}
      // onRowClick={(item) => {
      //   // Optional: implement detail view navigation if needed
      //   console.log('Transaction clicked:', item.id)
      // }}
    />
  )
}
